import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Dropdown, Image } from 'antd';
import { useOpenCollectionView } from 'hooks/collection/useOpenCollectionView';
import * as Styled from './styles';
import { GraphThreeNodes } from 'icons';
import { EditSquare } from 'icons/EditSquare';
import { MenuDots } from 'icons/MenuDots';
import { Trash } from 'icons/Trash';
import placeholderBannerImg from 'assets/images/collection-cover-image.png';
import placeholderImage from 'assets/images/collection-fallback.webp';
import { ICollection } from 'interfaces';

export type CollectionCardProps = {
  collection: ICollection;
  onCardClick?: () => void;
  onEdit?: () => void;
  editable?: boolean;
  onDelete?: () => void;
  onShare?: () => void;
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  className?: string;
  isSelected?: boolean;
};

export const CollectionCard: FC<CollectionCardProps> = props => {
  const { t } = useTranslation();
  const { handleOpenCollection } = useOpenCollectionView();

  const {
    collection,
    editable,
    onEdit,
    onCardClick,
    onDelete,
    onShare,
    width,
    height,
    className,
    isSelected
  } = props;

  const handleCardClick = useCallback(() => {
    if (onCardClick) {
      onCardClick();
    } else {
      handleOpenCollection(collection.slug);
    }
  }, [onCardClick, collection, handleOpenCollection]);

  const actionMenu = useMemo(() => {
    const shareMenu = {
      key: 3,
      label: (
        <Styled.MenuItem onClick={onShare}>
          {t('generic.share')}
          <GraphThreeNodes />
        </Styled.MenuItem>
      )
    };

    const deleteMenu = {
      key: 2,
      label: (
        <Styled.MenuItem onClick={onDelete}>
          {t('generic.delete')}
          <Trash />
        </Styled.MenuItem>
      )
    };

    const editMenu = {
      key: 1,
      label: (
        <Styled.MenuItem onClick={onEdit}>
          {t('generic.edit')}
          <EditSquare />
        </Styled.MenuItem>
      )
    };

    return collection.total_supply === 0
      ? [shareMenu, editMenu, deleteMenu]
      : [shareMenu, editMenu];
  }, [collection]);

  const [bannerUrl, setBannerUrl] = useState(collection.featured_image);

  useEffect(() => {
    setBannerUrl(collection.featured_image);
  }, [collection.featured_image]);

  return (
    <Styled.Wrapper
      className={className}
      onClick={!editable ? handleCardClick : undefined}
      $width={width}
      $height={height}
      $isSelected={isSelected}
    >
      <Styled.CardContent onClick={editable ? handleCardClick : undefined}>
        <Image
          loading="lazy"
          preview={false}
          src={bannerUrl || placeholderBannerImg}
          onError={() =>
            setBannerUrl('https://via.placeholder.com/256x144?text=Collection')
          }
          style={{ height: 144, objectFit: 'cover' }}
        />
      </Styled.CardContent>

      <Styled.Meta>
        <Styled.CollectionLogoWrapper
          onClick={editable ? handleCardClick : undefined}
        >
          <Styled.CollectionLogo>
            <Avatar size={64} src={collection.logo_image || placeholderImage} />
          </Styled.CollectionLogo>
        </Styled.CollectionLogoWrapper>
        <Styled.DetailWrapper>
          <div className="collection-meta-info">
            <span className="collection-name">
              {collection.name || t('generic.noName')}
            </span>
            <span className="collection-nft-count">
              {t('collection.totalCollection', {
                count: collection.total_supply
              })}
            </span>
          </div>
          {editable && actionMenu.length > 0 && (
            <div onClick={e => e.stopPropagation()} style={{ zIndex: 1 }}>
              <Dropdown
                menu={{ items: actionMenu }}
                placement="bottomRight"
                getPopupContainer={(triggerNode: HTMLElement): HTMLElement =>
                  triggerNode
                }
                arrow
                trigger={['click']}
              >
                <Styled.CollectionCardMenuToggle>
                  <MenuDots />
                </Styled.CollectionCardMenuToggle>
              </Dropdown>
            </div>
          )}
        </Styled.DetailWrapper>
      </Styled.Meta>
    </Styled.Wrapper>
  );
};
